// In-app notification bell. PRD-01 §9.8.
// Real-time push via Socket.IO (HANDOFF C1); 5min polling als fallback.

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { getSocket } from "@/lib/socket";
import { authStore } from "@/lib/auth";

interface Notification {
  id: string;
  category: string;
  priority: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  title: string;
  body: string;
  payload: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}

interface ListResponse {
  items: Notification[];
  unreadCount: number;
}

const PRIORITY_STYLES: Record<Notification["priority"], string> = {
  CRITICAL: "bg-red-50 border-red-200",
  HIGH: "bg-orange-50 border-orange-200",
  MEDIUM: "bg-amber-50 border-amber-200",
  LOW: "bg-neutral-50 border-neutral-200",
};

export function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get<ListResponse>("/v1/notifications?limit=20"),
    refetchInterval: 5 * 60_000, // 5min fallback; real-time via socket.io
    refetchIntervalInBackground: false,
  });

  // Real-time push: invalideer cache zodra een nieuwe notification binnenkomt.
  useEffect(() => {
    if (!authStore.isAuthenticated()) return;
    const socket = getSocket();
    const onNotification = (): void => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    };
    socket.on("notification", onNotification);
    return () => {
      socket.off("notification", onNotification);
    };
  }, [qc]);

  const markRead = useMutation({
    mutationFn: (id: string) => api.patch(`/v1/notifications/${id}/read`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const markAllRead = useMutation({
    mutationFn: () => api.post("/v1/notifications/mark-all-read", {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const unreadCount = query.data?.unreadCount ?? 0;
  const items = query.data?.items ?? [];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg hover:bg-neutral-100 text-neutral-600"
        aria-label={t("notifications.openLabel", "Notificaties")}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[1rem] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold flex items-center justify-center">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white border border-neutral-200 rounded-xl shadow-lg overflow-hidden z-50">
          <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold">
              {t("notifications.title", "Notificaties")}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="text-xs text-brand-700 hover:text-brand-900 disabled:opacity-50"
              >
                {t("notifications.markAllRead", "Alles als gelezen")}
              </button>
            )}
          </header>

          <div className="max-h-96 overflow-y-auto">
            {query.isLoading ? (
              <p className="p-4 text-sm text-neutral-500">{t("loading", "Laden…")}</p>
            ) : items.length === 0 ? (
              <p className="p-4 text-sm text-neutral-500">
                {t("notifications.empty", "Geen notificaties.")}
              </p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => !n.readAt && markRead.mutate(n.id)}
                  className={`w-full text-left px-4 py-3 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors ${
                    !n.readAt ? "bg-blue-50/30" : ""
                  }`}
                >
                  <div
                    className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold mb-1 border ${PRIORITY_STYLES[n.priority]}`}
                  >
                    {n.priority}
                  </div>
                  <p className="text-sm font-medium text-neutral-900">{n.title}</p>
                  <p className="text-xs text-neutral-600 mt-0.5 line-clamp-2">{n.body}</p>
                  <p className="text-[10px] text-neutral-400 mt-1">
                    {new Date(n.createdAt).toLocaleString("nl-NL", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
